import { ChevronLeft, ChevronRight, Pencil, Trophy } from "lucide-react";
import { useCanAccess, useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { AgentChip } from "../AgentChip";
import {
  POINTS_LEGEND,
  monthLabel,
  monthStart,
  shiftMonth,
  sortRanking,
  type RankingRow,
} from "./callPlan";
import { setCallGoal, useRanking } from "./useCallPlan";

/**
 * Daily competition: the ranking starts from zero every day. "Mes" shows the
 * accumulated month (earlier months can be browsed). The bar is today's calls
 * against each person's daily goal.
 */
export const RankingCard = ({
  ranking,
  meId,
  onChanged,
}: {
  ranking: RankingRow[];
  meId?: Identifier;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [goalDraft, setGoalDraft] = useState("");
  const currentMonth = monthStart();
  const [scope, setScope] = useState<"day" | "month">("day");
  const [month, setMonth] = useState(currentMonth);
  const isDay = scope === "day";
  const isCurrent = isDay || month === currentMonth;
  const pastQuery = useRanking(month, !isCurrent);
  const rows = sortRanking(isCurrent ? ranking : (pastQuery.data ?? []), scope);
  const pointsOf = (row: RankingRow) => (isDay ? row.today_points : row.points);
  const leaderPoints = rows[0] ? pointsOf(rows[0]) : 0;

  const saveGoal = async (row: RankingRow) => {
    const goal = Number(goalDraft);
    setEditingId(null);
    if (!Number.isInteger(goal) || goal < 1 || goal === row.goal) return;
    try {
      await setCallGoal(row.id, goal);
      notify(`Objetivo de ${row.first_name}: ${goal} llamadas al día`, {
        type: "success",
      });
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    }
  };

  return (
    <section className="flex flex-col gap-4 rounded-3xl bg-neutral-900 p-5 text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-lime-300" />
          <h2 className="text-lg font-semibold first-letter:uppercase">
            {isDay ? "Ranking de hoy" : `Ranking de ${monthLabel(month)}`}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <div className="mr-1 flex rounded-full bg-white/10 p-0.5 text-xs">
            {(["day", "month"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setScope(option);
                  setMonth(currentMonth);
                }}
                className={cn(
                  "rounded-full px-2.5 py-0.5 font-medium",
                  scope === option
                    ? "bg-lime-300 text-neutral-900"
                    : "text-neutral-300 hover:text-white",
                )}
              >
                {option === "day" ? "Hoy" : "Mes"}
              </button>
            ))}
          </div>
          {isDay ? null : (
            <>
              <button
                type="button"
                onClick={() => setMonth(shiftMonth(month, -1))}
                className="rounded-full p-1 text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="Mes anterior"
                title="Mes anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setMonth(shiftMonth(month, 1))}
                disabled={isCurrent}
                className="rounded-full p-1 text-neutral-400 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Mes siguiente"
                title="Mes siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
      <p className="-mt-2 text-xs text-neutral-400">
        {isDay
          ? "Cada día se empieza de cero. La barra es el objetivo de llamadas de hoy; el acumulado del mes, en «Mes»."
          : isCurrent
            ? "Lo que lleva cada uno este mes. Los puntos no se borran."
            : "Mes cerrado: así quedó."}
      </p>

      {!isCurrent && pastQuery.isPending ? (
        <p className="text-sm text-neutral-400">Cargando…</p>
      ) : null}
      <ol className="flex flex-col gap-3">
        {rows.map((row, index) => {
          const ratio = Math.min(1, row.goal ? row.today_calls / row.goal : 0);
          const isLeader = index === 0 && leaderPoints > 0;
          const isMe = String(row.id) === String(meId);
          return (
            <li
              key={row.id}
              className={cn(
                "flex flex-col gap-2 rounded-2xl p-3",
                isLeader ? "bg-lime-300 text-neutral-900" : "bg-white/5",
              )}
            >
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "w-5 text-center text-sm font-bold",
                    isLeader ? "text-neutral-900" : "text-neutral-400",
                  )}
                >
                  {index + 1}
                </span>
                <AgentChip id={row.id} name={row.first_name} compact />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">
                    {row.first_name}
                    {isMe ? " (tú)" : ""}
                  </span>
                  <span
                    className={cn(
                      "text-xs",
                      isLeader ? "text-neutral-700" : "text-neutral-400",
                    )}
                  >
                    {isDay
                      ? `${row.today_calls} llamadas · ${row.today_contacts} hablados · ${row.today_sales} ventas`
                      : `${row.calls} llamadas · ${row.contacts} hablados · ${row.sales} ventas`}
                    {isCurrent && row.pending
                      ? ` · ${row.pending} en cola`
                      : ""}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-2xl font-bold leading-none">
                    {pointsOf(row)}
                  </span>
                  <span
                    className={cn(
                      "text-[11px]",
                      isLeader ? "text-neutral-700" : "text-neutral-400",
                    )}
                  >
                    {isDay
                      ? `puntos hoy · ${row.points} en el mes`
                      : isCurrent && row.today_points
                        ? `puntos · +${row.today_points} hoy`
                        : "puntos"}
                  </span>
                </div>
              </div>
              {isCurrent ? (
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "h-2 flex-1 overflow-hidden rounded-full",
                      isLeader ? "bg-neutral-900/15" : "bg-white/10",
                    )}
                  >
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-500",
                        isLeader ? "bg-neutral-900" : "bg-lime-300",
                      )}
                      style={{ width: `${ratio * 100}%` }}
                    />
                  </div>
                  {editingId === String(row.id) ? (
                    <input
                      autoFocus
                      type="number"
                      min={1}
                      value={goalDraft}
                      onChange={(event) => setGoalDraft(event.target.value)}
                      onBlur={() => saveGoal(row)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") saveGoal(row);
                        if (event.key === "Escape") setEditingId(null);
                      }}
                      className="w-14 rounded bg-white px-1 text-right text-xs text-neutral-900"
                      aria-label={`Objetivo de ${row.first_name}`}
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={!isAdmin}
                      onClick={() => {
                        setGoalDraft(String(row.goal));
                        setEditingId(String(row.id));
                      }}
                      className={cn(
                        "inline-flex items-center gap-1 text-xs tabular-nums",
                        isLeader ? "text-neutral-700" : "text-neutral-400",
                        isAdmin && "hover:underline",
                      )}
                      title={isAdmin ? "Cambiar objetivo diario" : undefined}
                    >
                      hoy {row.today_calls}/{row.goal}
                      {isAdmin ? <Pencil className="h-3 w-3" /> : null}
                    </button>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-neutral-400">Puntos: {POINTS_LEGEND}</p>
    </section>
  );
};
